import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { withHistory } from '@sama/rxjs-core';
import { isTextObjectState } from '../../../../cross-team-shared/annotation/shared/state/object-state';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import { getOrThrow } from '../../../../cross-team-shared/misc-utils/strict-record';
import {
  HighlightFeedbackErrorType,
  highlightFeedbackErrorTypes,
} from '../../../../team-apis/otterdam/feedback';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { HighlightMenuService } from '../../services/highlight-menu.service';
import { HighlightService } from '../../services/highlight.service';
import {
  addHighlightRange,
  isRangeSelection,
  removeHighlightRange,
} from './highlight-utils';

@Component({
  selector: 'h3-highlight-response',
  templateUrl: './highlight-response.component.html',
  styleUrl: './highlight-response.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightResponseComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input({ required: true }) text!: string;
  @Input({ required: true }) objectIndex!: number;
  @Input() editable = false;
  @Input() deletable = false;

  @ViewChild('container') containerRef!: ElementRef<HTMLSpanElement>;

  private store = inject(TaskWorkspaceStore);
  private browserControlService = inject(BrowserControlService);
  private highlightService = inject(HighlightService);
  private highlightMenuService = inject(HighlightMenuService);

  private textObjectIndex!: number;

  @HostListener('mouseup', ['$event'])
  handleMouseUp(event: MouseEvent) {
    const selection = this.browserControlService.getSelection();
    if (!isRangeSelection(selection)) {
      this.handleClick(event);
      return;
    }

    if (!this.editable) {
      return;
    }

    this.handleTextSelection(event);
  }

  @HostListener('mousemove', ['$event'])
  handleMouseMove(event: MouseEvent) {
    const textIndex = this.getTextIndexUnderMouse(event);
    if (!textIndex) {
      return;
    }

    this.highlightService.highlightFirstFeedback(
      this.textObjectIndex,
      textIndex,
    );
  }

  @HostListener('mouseleave')
  clearHighlightedAtTextEdge() {
    this.highlightService.clearHighlighted();
  }

  ngOnInit() {
    const { objects } = this.store.state();
    const responseObject = getOrThrow(objects, this.objectIndex);
    const textObject = responseObject.childObjectIds
      .map((childObjectId) => getOrThrow(objects, childObjectId))
      .find(isTextObjectState);
    assert(textObject, 'Response object should have a text object');
    this.textObjectIndex = textObject.index;
  }

  ngAfterViewInit() {
    this.setUpSubscriptions();
  }

  private handleClick(event: MouseEvent) {
    const textIndex = this.getTextIndexUnderMouse(event);
    if (!textIndex) {
      return;
    }

    const highlight = this.highlightService.selectFirstFeedback(
      this.textObjectIndex,
      textIndex,
    );
    if (!highlight || !this.deletable) {
      return;
    }

    this.highlightMenuService.open('delete', [event.clientX, event.clientY]);
  }

  private handleTextSelection(event: MouseEvent) {
    const selection = this.browserControlService.getSelection();
    assert(
      isRangeSelection(selection) && selection.rangeCount > 0,
      'Selection must be available to get range',
    );

    // Chromium based browsers do not support multiple range selection
    const selectionRange = selection.getRangeAt(0);
    this.highlightService.setInProgress({
      startIndex: selectionRange.startOffset,
      endIndex: selectionRange.endOffset,
      objectId: this.textObjectIndex,
      text: selection.toString(),
    });

    const clientRect = selectionRange.getClientRects()[0];
    assert(clientRect, 'Client rect should be available for a valid selection');
    const lineHeight = clientRect.height;
    const distanceSpaceFromMouse = lineHeight * 0.6;
    this.highlightMenuService.open('add', [
      event.clientX,
      event.clientY - distanceSpaceFromMouse,
    ]);

    window.getSelection()?.removeAllRanges();
  }

  private setUpSubscriptions() {
    this.subscribeToHighlightChanges();
    this.subscribeToInProgressHighlight();
    this.subscribeToHighlightSelection();
    this.subscribeToHighlightedHighlights();
  }

  private subscribeToHighlightChanges() {
    this.subscribeTo(
      this.highlightService.getResponseHighlights$(this.textObjectIndex),
      ([addedHighlights, removedHighlights]) => {
        removedHighlights.forEach((highlight) => {
          removeHighlightRange(
            this.getHighlightRange(highlight),
            this.getHighlightKey(highlight.errorTag),
          );
        });
        addedHighlights.forEach((highlight) => {
          addHighlightRange(
            this.getHighlightRange(highlight),
            this.getHighlightKey(highlight.errorTag),
          );
        });
      },
    );
  }

  private subscribeToInProgressHighlight() {
    this.subscribeTo(
      this.store('highlightState')('inProgress').$.pipe(withHistory(1)),
      ([current, previous]) => {
        if (previous && previous.objectId === this.textObjectIndex) {
          removeHighlightRange(this.getHighlightRange(previous), 'in-progress');
        }
        if (current && current.objectId === this.textObjectIndex) {
          addHighlightRange(this.getHighlightRange(current), 'in-progress');
        }
      },
    );
  }

  private subscribeToHighlightSelection() {
    this.subscribeTo(
      this.store('highlightState')('selected').$.pipe(withHistory(1)),
      ([current, previous]) => {
        if (previous && previous.objectId === this.textObjectIndex) {
          removeHighlightRange(
            this.getHighlightRange(previous),
            `selected-${this.getHighlightKey(previous.errorTag)}`,
          );
        }

        if (current && current.objectId === this.textObjectIndex) {
          addHighlightRange(
            this.getHighlightRange(current),
            `selected-${this.getHighlightKey(current.errorTag)}`,
          );
          this.browserControlService.scrollIntoView(
            this.containerRef.nativeElement,
            {
              block: 'center',
            },
          );
        }
      },
    );
  }

  private subscribeToHighlightedHighlights() {
    this.subscribeTo(
      this.store('highlightState')('highlighted').$.pipe(withHistory(1)),
      ([current, previous]) => {
        if (previous && previous.objectId === this.textObjectIndex) {
          removeHighlightRange(
            this.getHighlightRange(previous),
            `selected-${this.getHighlightKey(previous.errorTag)}`,
          );
        }

        if (current && current.objectId === this.textObjectIndex) {
          addHighlightRange(
            this.getHighlightRange(current),
            `selected-${this.getHighlightKey(current.errorTag)}`,
          );
        }
      },
    );
  }

  private getTextIndexUnderMouse(event: MouseEvent) {
    const caretPosition = document.caretPositionFromPoint(
      event.clientX,
      event.clientY,
    );
    const isCaretOverText =
      caretPosition && caretPosition.offsetNode.nodeType === Node.TEXT_NODE;
    if (!isCaretOverText) {
      return;
    }

    const highlightRange = this.getHighlightRange({
      startIndex: 0,
      endIndex: caretPosition.offset,
    });

    return highlightRange.toString().length;
  }

  private getHighlightKey(errorTag: HighlightFeedbackErrorType) {
    const typeIndex = highlightFeedbackErrorTypes.indexOf(errorTag);
    return `type-${typeIndex}`;
  }

  private getHighlightRange(highlightIndexes: {
    startIndex: number;
    endIndex: number;
  }) {
    const textContainer = this.containerRef.nativeElement.firstChild;
    assert(
      textContainer && textContainer.nodeType === Node.TEXT_NODE,
      'Container should be a defined text node',
    );

    const newRange = new Range();
    newRange.setStart(textContainer, highlightIndexes.startIndex);
    newRange.setEnd(textContainer, highlightIndexes.endIndex);

    return newRange;
  }
}
